import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.color || '#2196F3'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: visible;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    z-index: 10;
  }
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(43, 43, 43, 0.9), rgba(0, 0, 0, 0.8));
  color: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  padding: 16px;
  text-align: center;
  z-index: 25;
  backdrop-filter: blur(2px);
  
  ${Card}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const InfoText = styled.div`
  font-weight: 600;
  word-break: break-word;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  white-space: pre-line;
  text-align: center;
  max-width: 100%;
  
  /* Dynamic font sizing for overlay */
  font-size: clamp(0.7rem, 2.5cqw, 1rem);
  
  /* Fallback for browsers without container query support */
  @supports not (font-size: 1cqw) {
    font-size: clamp(0.7rem, 2.5vw, 0.9rem);
  }
`;

const QuestionMark = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  cursor: help;
  z-index: 30;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 25px;
  right: 0;
  background: #2c3e50;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: clamp(0.65rem, 1.8vw, 0.75rem);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  width: 280px;
  white-space: normal;
  line-height: 1.4;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  
  /* Arrow pointing to question mark */
  &::before {
    content: '';
    position: absolute;
    top: 100%;
    right: 9px;
    border: 6px solid transparent;
    border-top-color: #2c3e50;
  }
  
  /* Adjust position if tooltip would go off-screen */
  @media (max-width: 768px) {
    width: 250px;
    right: -50px;
    
    &::before {
      right: 59px;
    }
  }
  
  @media (max-width: 480px) {
    width: 200px;
    right: -80px;
    
    &::before {
      right: 89px;
    }
  }
  
  ${QuestionMark}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(-5px);
    z-index: 1001;
  }
`;

const CardValue = styled.div`
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
  
  /* Much larger font sizing like before */
  font-size: clamp(1.5rem, 5cqw, 2.2rem);
  
  /* Fallback for browsers without container query support */
  @supports not (font-size: 1cqw) {
    font-size: clamp(1.5rem, 5vw, 1.8rem);
  }
  
  /* Adjust for very long text */
  ${props => props.textLength > 8 && `
    font-size: clamp(1rem, 4cqw, 1.8rem);
    
    @supports not (font-size: 1cqw) {
      font-size: clamp(1rem, 4vw, 1.5rem);
    }
  `}
  
  /* Adjust for very short text */
  ${props => props.textLength <= 4 && `
    font-size: clamp(1.4rem, 6cqw, 2.5rem);
    
    @supports not (font-size: 1cqw) {
      font-size: clamp(1.4rem, 6vw, 2rem);
    }
  `}
`;

const CardLabel = styled.div`
  color: #666;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  width: 100%;
  
  /* Slightly larger font sizing for labels */
  font-size: clamp(0.75rem, 2.5cqw, 0.9rem);
  
  /* Fallback for browsers without container query support */
  @supports not (font-size: 1cqw) {
    font-size: clamp(0.75rem, 2.5vw, 0.85rem);
  }
`;

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '0s';
  
  const totalSeconds = Math.round(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  // Prioritize most significant units for compact display
  if (days > 0) {
    if (hours > 0) {
      return `${days}d ${hours}h`;
    }
    return `${days}d`;
  }
  
  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h`;
  }
  
  if (minutes > 0) {
    if (secs > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${minutes}m`;
  }
  
  return `${secs}s`;
};

const formatNumber = (value) => {
  if (!value && value !== 0) return '0';
  
  // Handle decimal numbers (for averages)
  if (value % 1 !== 0) {
    return parseFloat(value.toFixed(1)).toString();
  }
  
  // Handle whole numbers with commas for large values
  return Math.floor(value).toLocaleString();
};

const getExactValue = (value, format, exactValueFormatter, additionalInfo) => {
  // Simply return the additional info if provided, otherwise empty string
  return additionalInfo || '';
};

const MetricCard = ({ 
  value, 
  label, 
  format = 'number', 
  color, 
  tooltip,
  additionalInfo,
  displayValueFormatter,
  exactValueFormatter,
  showQuestionMark = true,
  ...props 
}) => {
  let displayValue;
  
  // Use custom formatter if provided
  if (displayValueFormatter) {
    displayValue = displayValueFormatter(value);
  } else if (format === 'time') {
    displayValue = formatTime(value);
  } else {
    displayValue = formatNumber(value);
  }
  
  const infoText = getExactValue(value, format, exactValueFormatter, additionalInfo);
  const textLength = displayValue.toString().length;
  
  return (
    <Card color={color} {...props}>
      {showQuestionMark && tooltip && (
        <QuestionMark>
          ?
          <Tooltip>{tooltip}</Tooltip>
        </QuestionMark>
      )}
      <CardValue textLength={textLength}>{displayValue}</CardValue>
      <CardLabel>{label}</CardLabel>
      {infoText && (
        <HoverOverlay>
          <InfoText dangerouslySetInnerHTML={{ __html: infoText }} />
        </HoverOverlay>
      )}
    </Card>
  );
};

MetricCard.propTypes = {
  value: PropTypes.number,
  label: PropTypes.string.isRequired,
  format: PropTypes.oneOf(['number', 'time', 'custom']),
  color: PropTypes.string,
  tooltip: PropTypes.string,
  additionalInfo: PropTypes.string,
  displayValueFormatter: PropTypes.func,
  exactValueFormatter: PropTypes.func,
  showQuestionMark: PropTypes.bool
};

export { MetricCard };
